import { STICKY_HEADER_OFFSET } from 'components/pages/product/helpers';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useScrollToElement } from 'typescript/hooks/use_scroll_to_element';
import { useAddOns } from 'typescript/pages/product/hooks/use_add_ons';
import { useUpdateItemQuantity } from 'typescript/pages/product/hooks/use_update_item_quantity';

// TODO: we'll eventually need to define a proper type for the product
// and we might be able to just use the RPC model for this eventually but because
// the props we send from app/helpers/sq/web/product_props_helper.rb are so dynamic,
// it's easier to just use a Record<string, any> for now until we have a better solution
interface ProductContextProps {
  children: ReactNode;
  product: Record<string, any>;
  initialCart: Record<string, any>;
  experiments: Record<string, any>;
}

interface ProductContextType {
  product: Record<string, any>;
  currentProduct: Record<string, any> | null;
  setCurrentProduct: (product: Record<string, any>) => void;
  selection: {
    quantity: number;
    addOns: Record<string, string>;
  };
  setSelection: (selection: {
    quantity: number;
    addOns: Record<string, string>;
  }) => void;
  addOns: Record<string, any>;
  isGetAddOnsLoading: boolean;
  isGetAddOnsError: boolean;
  cart: Record<string, any>;
  experiments: Record<string, any>;
  onUpdateItemQuantity: ReturnType<
    typeof useUpdateItemQuantity
  >['onUpdateItemQuantity'];
  isUpdateItemQuantityLoading: boolean;
  accordionRef: React.RefObject<HTMLDivElement>;
  mainProductAdded: boolean;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error(
      'useProductContext must be used within a ProductContextProvider',
    );
  }
  return context;
};

const ProductContextProvider = ({
  children,
  product,
  initialCart,
  experiments,
}: ProductContextProps) => {
  // current product is the product that is currently being viewed
  // this could be the single product on the page or the selected alternative
  const [currentProduct, setCurrentProduct] = useState<Record<
    string,
    any
  > | null>(product);

  // selection is the quantity and add ons that are currently selected
  // ideally we eventually use react-hook-form to manage this but for now we'll just use a local state
  const [selection, setSelection] = useState<{
    quantity: number;
    addOns: Record<string, string>;
  }>({
    quantity: 1,
    addOns: {},
  });

  // initial cart is the one that is passed in from our ruby backend
  // as items get added to cart, we'll update the cart state and use that instead of
  // tracking things like item count and subtotal using additional state
  // ideally in the future, we can also use react-query to manage the cart state
  const [cart, setCart] = useState<Record<string, any> | null>(initialCart);

  // tracks whether the main product has been added to cart (for showing upsell accordion)
  const [mainProductAdded, setMainProductAdded] = useState<boolean>(false);

  // reset mainProductAdded when currentProduct changes (alternative selection)
  useEffect(() => {
    setMainProductAdded(false);
  }, [currentProduct?.slug]);

  const { addOns, isGetAddOnsLoading, isGetAddOnsError } = useAddOns({
    product,
    currentProduct,
    cart,
    experiments,
  });

  const { elementRef, scrollToElement } =
    useScrollToElement(STICKY_HEADER_OFFSET);

  // Memoize callback to maintain stable reference and prevent unnecessary re-renders
  const handleMainProductAdded = useCallback(() => {
    setMainProductAdded(true);
    scrollToElement();
  }, [scrollToElement]);

  const { onUpdateItemQuantity, isUpdateItemQuantityLoading } =
    useUpdateItemQuantity({
      currentProduct,
      cart,
      selection,
      addOns,
      setCart,
      onMainProductAdded: handleMainProductAdded,
    });

  // Memoize the entire context value to prevent unnecessary re-renders
  // Only updates when dependencies actually change
  const contextValue = useMemo(
    () => ({
      product,
      currentProduct,
      setCurrentProduct,
      selection,
      setSelection,
      addOns,
      isGetAddOnsLoading,
      isGetAddOnsError,
      cart,
      experiments,
      onUpdateItemQuantity,
      isUpdateItemQuantityLoading,
      accordionRef: elementRef,
      mainProductAdded,
    }),
    [
      product,
      currentProduct,
      setCurrentProduct,
      selection,
      setSelection,
      addOns,
      isGetAddOnsLoading,
      isGetAddOnsError,
      cart,
      experiments,
      onUpdateItemQuantity,
      isUpdateItemQuantityLoading,
      mainProductAdded,
    ],
  );

  return (
    <ProductContext.Provider value={contextValue}>
      {children}
    </ProductContext.Provider>
  );
};

export { ProductContextProvider };
